import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ShieldCheck, KeyRound, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import StickyHeader from "@/components/StickyHeader";
import { formatWIB } from "@/lib/datetime";
import { useAuth } from "@/context/AuthContext";
import { isAdminInstalasi } from "@/lib/roles";
import GantiKataSandiCard from "@/components/GantiKataSandiCard";

function CopyButton({ value }) {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            toast.error("Gagal menyalin. Salin manual saja.");
        }
    };
    return (
        <button type="button" onClick={copy} className="text-slate-400 hover:text-[#00877D] shrink-0">
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
    );
}

// Super Admin: authenticator (TOTP). Admin Instalasi: ganti kata sandi (keputusan pemilik,
// Okt 2026) -- peran lain tidak membuka halaman ini (roles di App.js).
export default function PengaturanKeamananAkun() {
    const { user } = useAuth();
    if (!isAdminInstalasi(user?.role)) return <KeamananAuthenticator />;
    return (
        <div className="max-w-3xl mx-auto px-6 lg:px-8 py-8 space-y-4">
            <StickyHeader>
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Keamanan Akun</div>
                    <h1 className="text-3xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Plus Jakarta Sans" }}>
                        Kata Sandi
                    </h1>
                </div>
            </StickyHeader>
            <GantiKataSandiCard />
        </div>
    );
}

function KeamananAuthenticator() {
    const [status, setStatus] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [currentCode, setCurrentCode] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [newCodes, setNewCodes] = useState(null);

    const load = async () => {
        try {
            const { data } = await api.get("/auth/totp/status");
            setStatus(data);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal memuat status authenticator"); }
    };
    useEffect(() => { load(); }, []);

    const openRegenerate = () => { setCurrentCode(""); setNewCodes(null); setDialogOpen(true); };

    const submitRegenerate = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const { data } = await api.post("/auth/totp/recovery-codes/regenerate", { code: currentCode });
            setNewCodes(data.recovery_codes);
            load();
        } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="max-w-3xl mx-auto px-6 lg:px-8 py-8 space-y-4">
            <StickyHeader>
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Autentikator (2FA)</div>
                    <h1 className="text-3xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Plus Jakarta Sans" }}>
                        Authenticator (TOTP)
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Verifikasi 2 langkah wajib untuk akun Super Admin lewat aplikasi authenticator.
                    </p>
                </div>
            </StickyHeader>

            <Card className="border-slate-200 shadow-sm">
                <CardContent className="p-6 space-y-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-teal-50 flex items-center justify-center">
                            <ShieldCheck className="w-5 h-5 text-[#00877D]" />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <span className="font-semibold text-slate-800">Status Authenticator</span>
                                {status && (
                                    <Badge className={status.totp_enabled ? "bg-emerald-100 text-emerald-700 border-emerald-300" : "bg-slate-100 text-slate-500 border-slate-300"}>
                                        {status.totp_enabled ? "Aktif" : "Nonaktif"}
                                    </Badge>
                                )}
                            </div>
                            {status?.totp_enrolled_at && (
                                <p className="text-xs text-slate-500 mt-0.5">Didaftarkan {formatWIB(status.totp_enrolled_at)}</p>
                            )}
                        </div>
                    </div>

                    {status?.totp_enabled && (
                        <div className="flex items-center justify-between border-t border-slate-100 pt-4">
                            <div>
                                <div className="text-sm font-semibold text-slate-700">Kode Pemulihan</div>
                                <p className="text-xs text-slate-500 mt-0.5">
                                    Sisa {status.recovery_codes_remaining} dari 10 kode belum terpakai.
                                </p>
                            </div>
                            <Button variant="outline" onClick={openRegenerate} data-testid="totp-regenerate-button" className="border-teal-300 text-teal-800 hover:bg-teal-50">
                                <KeyRound className="w-4 h-4 mr-2" /> Buat Ulang
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>

            <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) { setNewCodes(null); setCurrentCode(""); } }}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Buat Ulang Kode Pemulihan</DialogTitle></DialogHeader>
                    {newCodes ? (
                        <>
                            <p className="text-sm text-slate-600 mb-3">
                                10 kode pemulihan baru berhasil dibuat. <b>Kode lama sudah tidak berlaku.</b> Simpan kode
                                ini sekarang -- tidak akan ditampilkan lagi setelah dialog ini ditutup.
                            </p>
                            <div className="grid grid-cols-2 gap-2 bg-slate-50 border border-slate-200 rounded-lg p-3">
                                {newCodes.map((rc) => (
                                    <div key={rc} className="flex items-center justify-between gap-1.5 font-mono text-xs bg-white border border-slate-200 rounded px-2 py-1.5">
                                        <span>{rc}</span>
                                        <CopyButton value={rc} />
                                    </div>
                                ))}
                            </div>
                            <DialogFooter>
                                <Button onClick={() => setDialogOpen(false)} className="bg-[#00B9AD] hover:bg-[#60C0D0]">Tutup, Sudah Disimpan</Button>
                            </DialogFooter>
                        </>
                    ) : (
                        <form onSubmit={submitRegenerate} className="space-y-4">
                            <p className="text-sm text-slate-500">
                                Masukkan kode 6 digit dari aplikasi authenticator Anda saat ini untuk mengonfirmasi.
                            </p>
                            <div>
                                <Label className="text-xs font-semibold uppercase text-slate-600">Kode Autentikator</Label>
                                <Input
                                    data-testid="totp-regenerate-code-input"
                                    inputMode="numeric"
                                    maxLength={6}
                                    required
                                    autoFocus
                                    value={currentCode}
                                    onChange={(e) => setCurrentCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                    className="mt-1.5 h-11 text-center text-xl tracking-[0.4em] font-semibold"
                                />
                            </div>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Batal</Button>
                                <Button type="submit" disabled={submitting || currentCode.length !== 6} className="bg-[#00B9AD] hover:bg-[#60C0D0]">
                                    {submitting ? "Memproses..." : "Konfirmasi"}
                                </Button>
                            </DialogFooter>
                        </form>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
